import { TextAreaWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core'
import LockIcon from '@material-ui/icons/Lock'
import Alert from '@material-ui/lab/Alert'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { buildFeatureSet, getCredentials } from '../../cache/auth'
import Breadcrumbs from '../../components/Breadcrumb'
import PageHeader from '../../components/PageHeader'
import SLAChip from '../../components/SLAChip'
import SectionTitle from '../../components/SectionTitle'
import VisuallyHidden from '../../components/VisuallyHidden'
import {
  ADD_TICKET_COMMENT_MUTATION,
  ASSIGN_TICKET_MUTATION,
  TICKET_COMMENTS_QUERY,
  TICKET_EXTERNAL_CONTACT_QUERY,
  TICKET_QUERY,
  TRANSITION_TICKET_MUTATION
} from '../../graphql/tickets'
import { SUPPORT_PAGE_LABELS } from '../../helpers/supportLaunchContext'
import {
  commentRecipientHint,
  commentScopeBadge,
  commentScopesForRole,
  commentSubmitLabel
} from '../../helpers/ticketCommentScopes'
import {
  assignmentKind,
  assignmentLabel,
  nextStates,
  stateTone,
  ticketStateLabel,
  Ticket,
  TicketCommentList,
  TicketCommentScope,
  TicketResolutionCode,
  TicketState
} from '../../helpers/ticketWorkflow'
import { DEFAULT_PAGE_SIZE } from '../../helpers/useCursorPage'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { ROLE } from '../../staticRoleManagement/types'
import TicketPriority from '../SupportTickets/TicketPriority'
import CommentThread from './CommentThread'
import DiagnosticContextPanel from './DiagnosticContextPanel'
import RelatedRecordsPanel from './RelatedRecordsPanel'
import ReporterIdentity from './ReporterIdentity'
import ResolveDialog from './ResolveDialog'
import SectionCard from './SectionCard'
import SlaPanel from './SlaPanel'
import TicketActivity from './TicketActivity'
import TicketWatchers from './TicketWatchers'
import When from './When'
import useStyles from './styles/index.styles'

export default function SupportTicketDetail() {
  const classes = useStyles()
  const { id } = useParams<{ id: string }>()
  const [body, setBody] = useState('')
  const [commentScope, setCommentScope] =
    useState<TicketCommentScope>('PARTICIPANT_PUBLIC')
  const [resolving, setResolving] = useState(false)
  const role = getRoleFromStorage()
  const isAdmin = role === ROLE.ADMIN
  const canInspectDiagnostics =
    isAdmin && buildFeatureSet(getCredentials()).has('monitoring.view')
  const commentScopes = commentScopesForRole(role)

  const { data, loading, error } = useQuery<{ ticket: Ticket }>(TICKET_QUERY, {
    variables: { id },
    fetchPolicy: 'cache-and-network'
  })
  const ticket = data?.ticket
  // watcherUserIds is the same array TicketWatchers renders one chip per, so
  // the number named here can never disagree with the panel it refers to.
  const isInternal = commentScope === 'ADOTEL_INTERNAL'
  const recipientHint = commentRecipientHint(
    commentScope,
    ticket?.watcherUserIds.length ?? 0
  )
  const scopeBadge = commentScopeBadge(commentScope)
  // The whole composer takes the scope's tint, not just the badge — the
  // surface being typed into is the thing in view while the note is written.
  const scopeClass = isInternal
    ? classes.composerInternal
    : classes.composerPublic
  const { data: contactData, error: contactError } = useQuery<{
    ticketExternalContact: {
      normalizedName: string
      email?: string | null
      phoneE164?: string | null
    }
  }>(TICKET_EXTERNAL_CONTACT_QUERY, {
    variables: { ticketId: id },
    skip: !isAdmin || ticket?.reporter?.type !== 'REPORTER_TYPE_EXTERNAL'
  })

  // Both mutations return the full `TicketFields` selection, which includes
  // `__typename` and `id`. The cache normalises on that pair — `addTypename:
  // false` only stops Apollo INSERTING `__typename` into outgoing queries, it
  // does not disable normalisation for a selection that asks for it. So the
  // mutation response alone updates the `Ticket:<id>` entry that the mounted
  // TICKET_QUERY above is watching, and `refetchQueries` would be a second
  // round trip for data the client already holds. Verified by writing a
  // mutation result into a real InMemoryCache and reading the query back: the
  // query entry moved to the mutation's state and version.
  const [transition, transitionState] = useMutation<{
    transitionTicket: Ticket
  }>(TRANSITION_TICKET_MUTATION)
  const [addComment, commentState] = useMutation<{
    addTicketComment: { id: string }
  }>(ADD_TICKET_COMMENT_MUTATION)
  const [assign, assignState] = useMutation<{ assignTicket: Ticket }>(
    ASSIGN_TICKET_MUTATION
  )

  // ONE busy flag across both ticket-mutating actions, not one per mutation.
  // `version` is a per-ticket optimistic-concurrency token, so two writes in
  // flight at once necessarily carry the same captured value and the second is
  // rejected — a conflict the user caused by clicking two enabled buttons, not
  // by racing anyone. Per-mutation flags leave exactly that hole open.
  const mutating = transitionState.loading || assignState.loading

  function sendTransition(
    to: TicketState,
    resolutionCode?: TicketResolutionCode,
    resolutionNotes?: string
  ) {
    if (!ticket) return
    // `version` comes from the ticket on screen. If someone else moved it since
    // this render, the server rejects the stale token rather than clobbering
    // their change, and the error surfaces rather than being retried silently.
    transition({
      variables: {
        id: ticket.id,
        to,
        version: ticket.version,
        // Sent only for RESOLVED. ticket-service rejects a resolution on any
        // other target, so passing it unconditionally would break the
        // transitions that currently work.
        resolutionCode: resolutionCode ?? null,
        resolutionNotes: resolutionNotes || null
      }
    })
      .then(() =>
        // §7: mutation success -> toast.success, on 81 screens. It was absent
        // here, so the sighted path was silent on success and spoke only on
        // failure — the inverse of the VisuallyHidden live region below, which
        // announces the new state to a screen reader. An operator clicking
        // Open saw nothing happen and clicked again.
        {
          setResolving(false)
          toast.success(
            getTranslate('Ticket moved to {state}.', {
              state: ticketStateLabel(to)
            })
          )
        }
      )
      // The dialog stays open on failure. A stale-version rejection is
      // recoverable, and closing it would discard a resolution the operator
      // has already chosen.
      .catch((e) => toast.error(e.message))
  }

  /**
   * Move the ticket, asking first when the target needs an answer.
   *
   * RESOLVED is the only transition ticket-service will not accept on its own —
   * it requires a resolution code, and returns "resolving a ticket requires a
   * resolution code" without one. So Resolve opens the dialog and the actual
   * transition happens on confirm. Every other target still moves on one click.
   */
  function move(to: TicketState) {
    if (!ticket) return
    if (to === 'RESOLVED') {
      setResolving(true)
      return
    }
    sendTransition(to)
  }

  function escalate() {
    if (!ticket) return
    // Escalation hands the ticket to the Adotel-admin TIER, not to a person —
    // the service clears assigneeUserId as part of the same write. Sending an
    // assignee here alongside toAdotelAdmin would violate the exclusivity the
    // service enforces with a CHECK constraint, so only the flag is sent.
    assign({
      variables: {
        id: ticket.id,
        toAdotelAdmin: true,
        version: ticket.version
      }
    })
      .then(() => toast.success(getTranslate('Escalated to Adotel.')))
      .catch((e) => toast.error(e.message))
  }

  function submitComment(event: React.FormEvent) {
    event.preventDefault()
    if (!ticket || !body.trim()) return
    // The button is aria-disabled rather than disabled, so it still receives
    // clicks. This is the refusal; the attribute only announces it.
    if (commentState.loading) return
    // What was actually sent. The success handler clears the composer, and it
    // has to clear THIS text — an operator who starts their next comment while
    // the first is still in flight would otherwise have it wiped by a response
    // to something they already sent.
    const submitted = body
    addComment({
      variables: { ticketId: ticket.id, scope: commentScope, body: submitted },
      // Append into the cache rather than refetching page 1.
      //
      // A refetch here used the SAME variables as the thread's initial query,
      // and fetchMore's updateQuery writes its merged multi-page result back
      // under exactly those variables — so the refetch overwrote the whole
      // accumulated thread with a bare first page. Reproduced against a real
      // InMemoryCache: a slot holding three comments came back holding two
      // after a page-1 write. An agent who had loaded older pages and then
      // commented would silently lose them.
      update: (cache, { data: added }) => {
        const created = added?.addTicketComment
        if (!created) return
        // DEFAULT_PAGE_SIZE, not a literal 20. These variables are the cache
        // KEY, and CommentThread does not pass a page size at all — it takes
        // useCursorPage's default. So this literal was matching a default in a
        // third file, and changing that default would have made every readQuery
        // here miss silently: no error, no append, the comment simply absent
        // until something else refetched. One name now, in the file that owns
        // the value.
        const variables = { ticketId: ticket.id, pageSize: DEFAULT_PAGE_SIZE }
        const prev = cache.readQuery<{ ticketComments: TicketCommentList }>({
          query: TICKET_COMMENTS_QUERY,
          variables
        })
        // Nothing cached yet means the thread has not been read; the query
        // will fetch it fresh, and writing a one-comment thread here would
        // make it look complete.
        if (!prev?.ticketComments) return
        cache.writeQuery({
          query: TICKET_COMMENTS_QUERY,
          variables,
          data: {
            ticketComments: {
              ...prev.ticketComments,
              comments: [...prev.ticketComments.comments, created]
            }
          }
        })
      }
    })
      .then(() => {
        // Only clear what was sent. If the operator typed on while the mutation
        // was in flight, their newer text stays.
        setBody((current) => (current === submitted ? '' : current))
        // Focus returns to the composer. The submit button is aria-disabled the
        // instant the body empties, and a native `disabled` would have dropped
        // focus to <body>; even with aria-disabled the useful destination after
        // sending is the box you type the next one into, not the button you
        // just pressed. Queried by id rather than a ref because
        // TextAreaWithLabel is a third-party wrapper with no documented ref
        // forwarding — the id is already contractual, referenced by tests and
        // Cypress.
        document.getElementById('body')?.focus()
        toast.success(getTranslate('Comment added.'))
      })
      .catch((e) => toast.error(e.message))
  }

  /**
   * Ctrl/Cmd + Enter sends, from inside the textarea.
   *
   * metaKey OR ctrlKey, not one or the other: the panel runs on both, and
   * picking a single modifier makes the shortcut silently absent on half the
   * machines. Enter alone deliberately still inserts a newline — a comment is
   * prose, and a bare-Enter send is how people accidentally post half a
   * sentence to a customer.
   *
   * The guard mirrors the submit button's `disabled` exactly, so the shortcut
   * cannot do something the button would refuse.
   */
  function onComposerKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key !== 'Enter') return
    if (!event.metaKey && !event.ctrlKey) return
    // preventDefault BEFORE the guard, not after. With it after, a rejected
    // shortcut fell through to the browser default and inserted a newline — so
    // the user's "send" gesture visibly grew the box instead of doing nothing.
    // The gesture is consumed either way; only whether it sends is conditional.
    event.preventDefault()
    if (commentState.loading || !body.trim()) return
    submitComment(event)
  }

  if (loading && !ticket) {
    return (
      <>
        <CircularProgress size={24} />
        <VisuallyHidden live="polite">{getTranslate('Loading')}</VisuallyHidden>
      </>
    )
  }
  if (error) {
    return <Alert severity="error">{error.message}</Alert>
  }
  if (!ticket) {
    // A ticket the caller may not see returns NOT FOUND from the service — the
    // same response as one that does not exist. Saying "you do not have access"
    // here would confirm it exists and undo the existence-leak protection the
    // whole stack maintains.
    return <Alert severity="info">{getTranslate('Ticket not found.')}</Alert>
  }

  return (
    <div>
      <PageHeader title={ticket.ticketNumber} />

      <div style={{ marginBottom: 16 }}>
        {/* §4.2: Breadcrumb is the default for a detail page reached by drilling
          down, on 17 screens. Without it this screen had NO visible route back
          to the queue and no location cue — PageHeader renders nothing, and
          the SectionTitle below reads as a page heading rather than a position.
          Someone arriving from a notification link had browser-back only. */}
        <Breadcrumbs
          levels={[
            { label: getTranslate('Support'), link: '/support' },
            { label: ticket?.ticketNumber || '' }
          ]}
          loading={loading}
        />
        <SectionTitle title={ticket.title} marginBottom />
        <Typography variant="body2" color="textSecondary">
          <bdi dir="ltr">{ticket.ticketNumber}</bdi> ·{' '}
          <bdi dir="ltr">
            {ticket.reporterEntityRef
              ? `${ticket.reporterEntityRef.type}:${ticket.reporterEntityRef.code}`
              : getTranslate('unknown entity')}
          </bdi>{' '}
          · <bdi dir="ltr">{ticket.productScope}</bdi>
        </Typography>
        {ticket.reporter?.type === 'REPORTER_TYPE_EXTERNAL' && isAdmin && (
          <Typography variant="body2" color="textSecondary">
            {contactError
              ? getTranslate('External contact unavailable.')
              : contactData?.ticketExternalContact
              ? `${contactData.ticketExternalContact.normalizedName} · ${
                  contactData.ticketExternalContact.email ||
                  contactData.ticketExternalContact.phoneE164
                }`
              : getTranslate('Loading external contact…')}
          </Typography>
        )}
        <div style={{ marginTop: 8 }}>
          {/* Priority leads the badges, because it is the first thing a triaging
              agent looks for and the only one that orders two tickets against
              each other.

              It was absent from this screen entirely while being fetched by the
              query and rendered in the queue — TicketPriority is the component
              the list rows already use, so the detail page and the row it came
              from now say the same thing the same way. Reusing it also carries
              its accessibility: a text label with the colour as a decorative,
              aria-hidden stripe, which is what keeps this row from becoming a
              line of colour-only meaning as more chips arrive. */}
          <TicketPriority priority={ticket.priority} />
          <Chip
            size="small"
            label={ticketStateLabel(ticket.state)}
            color={stateTone(ticket.state)}
          />
          <SLAChip ticket={ticket} />
          {/* Ownership, at the weight of an answer rather than of metadata.
              Who holds a ticket is one of the four things a triaging agent
              scans for, and it was grey body2 prose sandwiched between a
              24-character hex id and a creation date — the id being visually
              longer than the label it qualified. */}
          <Typography
            variant="body2"
            style={{ display: 'inline', marginInlineStart: 8 }}>
            <strong>{assignmentLabel(ticket)}</strong>
            <Typography variant="body2" color="textSecondary" component="span">
              {' · '}
              {getTranslate('opened')} <When iso={ticket.createdAt} />
            </Typography>
          </Typography>
        </div>
        {/* The full assignee id keeps its own line, at caption weight.
            Selectable, on the one screen with room for it — the list rows show
            a shortened form, dropping it entirely would leave it unreachable,
            and a `title` would make it mouse-only. All of that still holds; what
            changed is that it no longer sits inline outweighing the name it
            belongs to. */}
        {assignmentKind(ticket) === 'user' && (
          <Typography
            variant="caption"
            color="textSecondary"
            component="p"
            style={{ marginTop: 4, overflowWrap: 'anywhere' }}>
            <bdi dir="ltr">{ticket.assigneeUserId}</bdi>
          </Typography>
        )}
      </div>

      {/* Actions above the content, not as two cards midway down it.
          Previously "Move to" and "Escalate" were separate <Paper> blocks
          between the metadata and the conversation, so an agent scrolled past
          them to read and back up to act.

          A Paper, not a new container: §2's established surface. The row is
          BOUNDED, which is what makes a bar workable here rather than a menu —
          LEGAL_TRANSITIONS offers at most two next states from any state, plus
          one conditional Escalate. Three controls, worst case.

          Deliberately NOT sticky. That is a separate decision with its own cost
          on mobile, where the conversation is already cramped, and bundling it
          would make this change harder to judge. */}
      {/* Raised, and the heading is unconditional. The two cards this replaced
          contributed two permanent entries to the document outline; a
          conditional "Move to" contributed one that vanished in the
          escalate-only branch, leaving the ONLY actionable region on the page
          with no heading — unreachable by heading navigation, which is the mode
          the "move actions up" change exists for. SectionCard's required
          heading makes that branch unreachable. */}
      <SectionCard heading={getTranslate('Actions')} level="h2">
        {/* Live region, because every action button disables while `mutating`
            and the state Chip that changes is far up the page. Without this the
            success path is silent and the FAILURE path is the only one that
            speaks, via toast.error.

            Focus is still dropped when the button disables mid-flight — MUI
            sets the native disabled attribute, which removes it from the tab
            order. Retaining focus needs aria-disabled plus a click guard plus a
            disabled-look style, which is a visual change; backlogged rather
            than bundled here. */}
        <VisuallyHidden live="polite">
          {mutating
            ? getTranslate('Updating ticket…')
            : `${getTranslate('Ticket is now')} ${ticketStateLabel(
                ticket.state
              )}`}
        </VisuallyHidden>

        <div className={classes.actionBar}>
          {nextStates(ticket.state).length > 0 && (
            <div
              role="group"
              aria-labelledby="ticket-move-to-label"
              className={classes.actionGroup}>
              {/* A span, not a heading. A heading never labels adjacent content
                  programmatically — it only creates reading-order adjacency —
                  and a two-word fragment whose presence depends on ticket state
                  is a poor outline entry. role=group + aria-labelledby is the
                  relationship the heading only appeared to provide. */}
              <Typography
                variant="caption"
                color="textSecondary"
                component="span"
                id="ticket-move-to-label"
                className={classes.actionLabel}>
                {getTranslate('Move to')}
              </Typography>
              {/* Only legal next states are offered. The server validates
                  anyway — this stops the operator being handed a button that
                  can only fail. */}
              {nextStates(ticket.state).map((state) => (
                <Button
                  key={state}
                  size="small"
                  variant="outlined"
                  disabled={mutating}
                  // The accessible name carries the verb. A group label is
                  // announced on ENTRY to the group, which browser/SR pairs
                  // handle inconsistently — someone tabbing straight onto the
                  // control otherwise hears only "Resolved, button".
                  aria-label={`${getTranslate('Move to')} ${ticketStateLabel(
                    state
                  )}`}
                  onClick={() => move(state)}>
                  {ticketStateLabel(state)}
                </Button>
              ))}
            </div>
          )}

          {/* Rendered unconditionally rather than behind `resolving`, and the
              honest reason is that it is simpler — not that it has to be.
              MUI's Modal returns null while closed (keepMounted defaults false),
              so nothing is in the DOM either way, and the only cost of leaving
              it mounted is constructing a handful of element descriptors per
              parent render.
              
              An earlier version of this note claimed conditional mounting would
              skip the enter animation and would discard the chosen code
              mid-flight. Both were wrong. Dialog passes appear:true to its
              transition, which animates a component that mounts already open;
              and `resolving` only goes true→false in the success handler, so
              the dialog is never unmounted while the mutation is in flight or
              after it fails. The choice is fine; the reasoning was not. */}
          <ResolveDialog
            open={resolving}
            busy={mutating}
            onCancel={() => setResolving(false)}
            onConfirm={(code, notes) => sendTransition('RESOLVED', code, notes)}
          />

          {/* Offered only when the ticket is not already with Adotel —
              re-escalating an escalated ticket is a no-op the server would
              accept, which reads to the user as the button doing nothing.

              The card this replaces carried the heading "Need Adotel to look at
              this?", which was doing real work: it told an agent WHEN to use
              the action. A bar has no room for that, so the label carries it —
              hence the title, which repeats it for anyone who wants it. */}
          {assignmentKind(ticket) !== 'adotel-admin' && (
            <Button
              size="small"
              variant="outlined"
              disabled={mutating}
              aria-describedby="ticket-escalate-hint"
              onClick={escalate}>
              {getTranslate('Escalate to Adotel')}
            </Button>
          )}

          {/* No transitions AND no escalation is reachable — CLOSED offers
              OPEN, so today it cannot happen, but the branch would render an
              empty bar rather than a bare Paper if the graph ever changed. */}
          {!nextStates(ticket.state).length &&
            assignmentKind(ticket) === 'adotel-admin' && (
              // One key with a placeholder, not a fragment plus a value: Farsi
              // orders these differently and a translator cannot reorder across
              // a concatenation.
              <Typography variant="body2" color="textSecondary">
                {getTranslate('No actions available from {state}.', {
                  state: ticketStateLabel(ticket.state)
                })}
              </Typography>
            )}
        </div>

        {/* The card this replaced carried the heading "Need Adotel to look at
            this?", which told an agent WHEN to use the action. That moved to a
            `title`, which is mouse-only — no hover for keyboard users, and
            verbosity-dependent for screen readers. A visible caption wired as
            the button's description gives it back to everyone, for one line
            inside the Paper rather than the whole card. */}
        {assignmentKind(ticket) !== 'adotel-admin' && (
          <Typography
            id="ticket-escalate-hint"
            variant="caption"
            color="textSecondary"
            component="p"
            style={{ marginTop: 8 }}>
            {getTranslate('Escalate when Adotel needs to look at this.')}
          </Typography>
        )}
      </SectionCard>

      {/* Two columns from `md` up, one below. The conversation is the work;
          reporter, affected entities, service level, watchers and diagnostics
          are reference an agent consults occasionally, so they move OUT of the
          reading path rather than sitting above it — previously the thread
          rendered seventh, below five blocks of metadata.

          The header and the action bar stay full width. Actions were moved to
          the top in !1122 precisely so they are not buried mid-page, and
          dropping them into a narrow side column would undo that. */}
      <div className={classes.layout}>
        {/* A labelled region, mirroring the aside. Without it, landmark
            navigation could jump OUT of the conversation and offer nothing to
            jump back to — a one-way skip. Labelled BY the Conversation heading
            rather than with a second string, so the two cannot drift. */}
        <section className={classes.main} aria-labelledby="ticket-conversation">
          {/* The composer renders INSIDE the thread's Paper. It was a separate
            card below it, so the reply box and the message it answers were never
            on the same surface — and on a long ticket, never on screen together.

            The scope control is a RadioGroup. It was a collapsed
            SelectWithLabel, and the note here argued against replacing it —
            correctly, as far as it went: a BESPOKE segmented toggle would have
            been the local abstraction the reuse contract exists to prevent.
            RadioGroup is @material-ui/core, so that objection does not reach
            it, and placement was never the whole problem. A collapsed select
            shows the chosen audience only to someone who looks at it, and the
            Ctrl+Enter path never does. */}
          <CommentThread key={ticket.id} ticketId={ticket.id}>
            <Divider style={{ margin: '16px 0 8px' }} />
            {/* One level below Conversation, whatever that level is. The
              composer renders INSIDE the Conversation Paper, so a sibling level
              would claim it is a peer section while the DOM nests it — an
              outline that contradicts the structure. It was h4 under an h3
              Conversation; Conversation is h2 now that the two columns are the
              page's top-level structure, so this is h3. */}
            <Typography variant="subtitle2" component="h3">
              {getTranslate('Add a comment')}
            </Typography>
            <form onSubmit={submitComment} className={scopeClass}>
              {/* The audience, stated where the writing happens.

                  The radio group below is the control; this is the reminder.
                  They say the same thing on purpose — the control sits under
                  the textarea, so during a long note it is out of view for the
                  entire time the operator's belief about who is reading is
                  being formed. Text first: the lock icon and the tinted rule
                  are reinforcement, and 1.4.1 is satisfied without either. */}
              <Typography
                variant="body2"
                component="p"
                className={
                  scopeBadge.internal
                    ? classes.scopeBadgeInternal
                    : classes.scopeBadgePublic
                }>
                {scopeBadge.internal && (
                  <LockIcon
                    fontSize="small"
                    aria-hidden="true"
                    className={classes.scopeBadgeIcon}
                  />
                )}
                {getTranslate(scopeBadge.label)}
              </Typography>
              {/* `id` and `name` no longer have to agree. They did until
                @adotel/components 1.2.0, which rendered `<label htmlFor={name}>`
                while the control's id came from the prop spread; `useFieldId` now
                prefers `id`. Both are kept because the id is referenced by tests
                and Cypress, not to satisfy the old constraint.

                A placeholder still would not do: it names the field only until the
                operator types, and a screen reader then reaches an unnamed
                textarea. The instruction lives in helpertext, which persists. */}
              <TextAreaWithLabel
                id="body"
                name="body"
                rows={3}
                value={body}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setBody(e.currentTarget.value)
                }
                onKeyDown={onComposerKeyDown}
                label={getTranslate('Comment')}
                helpertext={getTranslate(
                  'Describe what changed or what you found. Ctrl or Cmd + Enter sends.'
                )}
              />
              {commentScopes.length > 1 && (
                /* A RadioGroup, replacing the collapsed Select.

                   The note this replaces rejected a BESPOKE segmented control,
                   and that reasoning stands — but RadioGroup is @material-ui/core,
                   the same vocabulary as the rest of this screen, so it costs the
                   reuse contract nothing. What it buys: both audiences are on
                   screen at once, so the operator reads a choice instead of
                   remembering to open one.

                   It also dissolves the aria-live problem the Select forced. A
                   Select returns focus to its trigger on change, re-announcing
                   the description; a live region would then say it twice, and
                   would fire unprompted mid-composition whenever the watcher
                   refetch changed the count. Radios announce their own checked
                   state on selection, so the consequence sentence can stay a
                   plain description and be read when the operator arrives at it. */
                <FormControl
                  component="fieldset"
                  style={{ marginTop: 8, display: 'block' }}>
                  <FormLabel component="legend" style={{ fontSize: '0.75rem' }}>
                    {getTranslate('Comment visibility')}
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-describedby="comment-recipients"
                    name="commentScope"
                    value={commentScope}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setCommentScope(event.target.value as TicketCommentScope)
                    }>
                    {commentScopes.map((scope) => (
                      <FormControlLabel
                        key={scope.value}
                        value={scope.value}
                        control={<Radio size="small" color="primary" />}
                        label={getTranslate(scope.label)}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}

              {/* Rendered whether or not the Select is — a non-admin has no
                  choice to make but still benefits from knowing the reporter
                  reads this. The consequence of the setting is not conditional
                  on being able to change the setting. A non-admin reaches it
                  in browse mode only, since with no Select there is nothing to
                  own the description; that is accepted rather than overlooked,
                  because a reader with exactly one possible scope has no
                  decision the sentence could change.

                  NOT a live region, deliberately. MUI v4's Select is a button
                  plus a menu, so changing the value always returns focus to
                  the trigger — which re-announces the description. A live
                  region on the same node would say the sentence a second time,
                  immediately, with nothing attaching it to the control. And
                  because the count derives from a cache-and-network query, it
                  would also fire unprompted mid-composition when a refetch
                  changed the watcher list. The description alone covers every
                  moment the operator is actually at the control.

                  The internal note gets real emphasis. It is the strongest
                  consequence statement on the screen, and caption/secondary is
                  this panel's tier for incidental metadata like a policy
                  version — a sighted operator scanning the composer skips it
                  there. Weight, not colour: colour alone would fail 1.4.1, and
                  the wording already carries the meaning. */}
              <Typography
                id="comment-recipients"
                variant={isInternal ? 'body2' : 'caption'}
                color={isInternal ? 'textPrimary' : 'textSecondary'}
                component="p"
                style={{ display: 'block', marginTop: 4 }}>
                {getTranslate(recipientHint.key, recipientHint.variables)}
              </Typography>
              {/* The label names the audience, so the scope is read at the
                  moment of commitment rather than beside the control that set
                  it. This is also the ONLY scope signal on the Ctrl+Enter path,
                  which never visits the radio group.

                  aria-disabled, not disabled: MUI v4 sets the native attribute,
                  which removes the node from the tab order — so on success the
                  button disables under the pointer and a keyboard user's focus
                  falls to <body>. The guard in submitComment is what actually
                  refuses the submit; this only communicates it. */}
              <Button
                type="submit"
                size="small"
                variant="contained"
                color="primary"
                style={{ marginTop: 8 }}
                aria-disabled={commentState.loading || !body.trim()}
                className={
                  commentState.loading || !body.trim()
                    ? classes.buttonDisabled
                    : undefined
                }>
                {getTranslate(commentSubmitLabel(commentScope))}
              </Button>
            </form>
          </CommentThread>
        </section>

        {/* A complementary landmark, and a NAMED one — so the reference
            column is skippable by landmark navigation instead of reading as
            more of the conversation. An unnamed <aside> is indistinguishable
            from any other in a landmark list. */}
        <aside
          className={classes.aside}
          aria-labelledby="ticket-reference-heading">
          {/* A real heading, not just an aria-label. Landmarks do NOT take part
              in the heading outline — that outline is the flat h1..h6 sequence —
              so with a label alone the three h3s below read as peers of
              `Actions`, and the only cue that the reader had left the
              conversation was an h4 -> h3 level RISE, indistinguishable from
              "the previous section ended". Heading navigation is the mode the
              `Actions` heading was added for, so leaving this column out of it
              reintroduces the defect that change fixed.

              aria-labelledby pointing at the visible heading rather than
              aria-label: the APG-preferred form, and one string instead of two
              that can drift apart. */}
          <Typography
            variant="subtitle2"
            component="h2"
            id="ticket-reference-heading">
            {getTranslate('Ticket properties')}
          </Typography>
          {ticket.sourcePageKey &&
            SUPPORT_PAGE_LABELS[ticket.sourcePageKey] && (
              <SectionCard
                heading={getTranslate('Reporting from')}
                level="h3"
                quiet>
                <Typography variant="body2">
                  {getTranslate(SUPPORT_PAGE_LABELS[ticket.sourcePageKey])}
                </Typography>
              </SectionCard>
            )}
          <RelatedRecordsPanel records={ticket.relatedRecords || []} />
          <SectionCard heading={getTranslate('Reported by')} level="h3" quiet>
            <ReporterIdentity reporter={ticket.reporter} />

            <Typography
              variant="subtitle2"
              component="h3"
              style={{ marginTop: 12 }}>
              {getTranslate('Affected')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {ticket.affectedEntityRefs.length
                ? ticket.affectedEntityRefs
                    .map((r) => `${r.type}:${r.code}`)
                    .map((reference) => (
                      <bdi dir="ltr" key={reference}>
                        {reference}
                      </bdi>
                    ))
                    .reduce<React.ReactNode[]>(
                      (items, reference, index) =>
                        index === 0
                          ? [reference]
                          : [...items, ' · ', reference],
                      []
                    )
                : getTranslate('none recorded')}
            </Typography>
          </SectionCard>

          {/* Above the watchers, because "by when" is a triage question and
              the watcher list is an administrative one. The deadlines used to
              live only in SLAChip, which stays silent unless a ticket is
              breached or close to it — right for a queue of hundreds, but it
              left the one screen where the question is actually asked with no
              way to answer it for a healthy ticket. */}
          <SlaPanel ticket={ticket} />

          <TicketWatchers ticket={ticket} />

          {ticket.hasDiagnosticContext && (
            <DiagnosticContextPanel
              ticketId={ticket.id}
              canInspect={canInspectDiagnostics}
            />
          )}

          {/* Unconditional, unlike the panel above it. Diagnostics render only
              when a TRACE context was attached; a ticket ALWAYS has a history,
              so an absent panel here would mean "you cannot see it", never
              "nothing happened" — the component draws that distinction itself
              by returning null when the caller cannot inspect. */}
          <TicketActivity
            ticketId={ticket.id}
            createdAt={ticket.createdAt}
            canInspect={canInspectDiagnostics}
          />
        </aside>
      </div>
    </div>
  )
}
